import _ from 'underscore';

import { DayPartHelper } from '@biteinc/business-rules';
import {
  BrinkApiServerHelper,
  CouponProvider,
  EigenApiServerHelper,
  GivexApiServerHelper,
  IntegrationInstanceTypeHelper,
  IntegrationSystem,
  LoyaltyAuthMethodHelper,
  OmnivorePosTypeHelper,
  PinataApiServerHelper,
  QsrAutomationsModeHelper,
  StarbucksConceptHelper,
} from '@biteinc/enums';
import { Time } from '@biteinc/helpers';

import { getBadgeHtml } from '../../helpers/html_builder';
import { TimeHelper } from '../../helpers/time_helper';

const campusCardMigrationBySourceSystem = {
  [IntegrationSystem.AtriumStoredValue]: {
    targetSystem: IntegrationSystem.AtriumCampusCard,
    migrateMethodName: 'migrateAtriumStoredValueToCampusCard',
    buttonLabel: 'Migrate Atrium (SV) to Campus Card',
    sourceLabel: 'Atrium (SV)',
    targetLabel: 'Atrium (Campus Card)',
  },
  [IntegrationSystem.AtriumLoyalty]: {
    targetSystem: IntegrationSystem.AtriumCampusCard,
    migrateMethodName: 'migrateAtriumLoyaltyToCampusCard',
    buttonLabel: 'Migrate Atrium (Loyalty) to Campus Card',
    sourceLabel: 'Atrium (Loyalty)',
    targetLabel: 'Atrium (Campus Card)',
  },
};

app.IntegrationCellView = app.CellView.extend({
  initialize() {
    app.CellView.prototype.initialize.apply(this, arguments);

    if (this.model.requiresOAuth() && !this.model.hasOAuthed()) {
      const $connectButton = this.addButtonWithClass(
        'btn-primary',
        this._connectButtonWasClicked.bind(this),
      );
      $connectButton.html(`Connect to ${this.model.displayName()}`);
    } else if (this.model.isMissingUserProvidedFields()) {
      const $editButton = this.addButtonWithClass(
        'btn-primary',
        this._editButtonWasClicked.bind(this),
      );
      $editButton.html('Add Credentials');
    } else if (
      this.model.requiresOAuth() &&
      this.model.hasOAuthed() &&
      this.model.canLoginToAccount()
    ) {
      const $viewAccountButton = this.addButtonWithClass(
        'btn-primary',
        this._viewAccountButtonWasClicked.bind(this),
      );
      $viewAccountButton.html(`View ${this.model.displayName()} Account`);
    } else if (this.model.requiresOAuth()) {
      const $connectButton = this.addButtonWithClass(
        'btn-primary',
        this._connectButtonWasClicked.bind(this),
      );
      $connectButton.html(`Re-Connect to ${this.model.displayName()}`);
    }

    if (app.sessionUser.canManageFullI9ns() && this.model.canBeSynced() && !this.model.isPosI9n()) {
      const $syncButton = this.addButtonWithClass(
        'btn-primary',
        this._syncButtonWasClicked.bind(this),
      );

      $syncButton.html('<i class="bi bi-arrow-clockwise"></i>&nbsp;Sync');
    }

    const campusCardMigration = this._getAvailableCampusCardMigration();
    if (campusCardMigration) {
      const $migrateButton = this.addButtonWithClass(
        'btn-warning',
        this._migrateToCampusCardButtonWasClicked.bind(this),
      );
      $migrateButton.addClass('campus-card-migration');
      $migrateButton.attr('title', campusCardMigration.buttonLabel);
      $migrateButton.html('<i class="bi bi-arrow-right-circle"></i><span>Migrate</span>');
    }

    if (
      app.sessionUser.isBite() &&
      this.model.canHaveOrgLevelWebhookSettings() &&
      !this.model.hasOrgLevelWebhookSettingsSetup()
    ) {
      const $setUpWebhookSettingsButton = this.addButtonWithClass(
        'btn-primary',
        this._setUpWebhooksButtonWasClicked.bind(this),
      );
      $setUpWebhookSettingsButton.html(`Set Up Webhooks`);
    }

    this.options.clickable = this.model.canBeManagedByUser();
    this.options.selectable = true;
  },

  _getAvailableCampusCardMigration() {
    if (!app.sessionUser.isBite() || !app.sessionUser.canManageFullI9ns()) {
      return null;
    }

    const migration = campusCardMigrationBySourceSystem[this.model.get('system')];
    if (!migration) {
      return null;
    }

    const integrations = this.model.collection?.models;
    if (!integrations) {
      return null;
    }

    const hasExistingCampusCardI9n = integrations.some((i9n) => {
      return i9n.get('system') === migration.targetSystem;
    });
    return hasExistingCampusCardI9n ? null : migration;
  },

  _migrateToCampusCardButtonWasClicked(event) {
    const migration = this._getAvailableCampusCardMigration();
    if (!migration) {
      return false;
    }

    const $button = $(event.currentTarget);
    const i9n = this.model;

    new app.AlertView().show(
      `Migrate ${migration.sourceLabel} to ${migration.targetLabel}?`,
      `This will create a ${migration.targetLabel} integration, copy matching Campus Card custom strings, and remove the ${migration.sourceLabel} integration.`,
      () => {
        const reqId = $button.initLoadingButton($button.html(), 'Migrating', 'Migrated');
        i9n[migration.migrateMethodName]((err, response) => {
          if (err) {
            $button.loadingDidFinishWithError(reqId);
            return;
          }

          $button.loadingDidFinishSuccessfully(reqId);
          const copiedCount = response.customStrings?.copied?.length || 0;
          const skippedCount = response.customStrings?.skippedExisting?.length || 0;
          const copiedText = copiedCount === 1 ? 'custom string' : 'custom strings';
          const skippedText =
            skippedCount === 1 ? 'existing Campus Card string' : 'existing Campus Card strings';
          app.showSavedToastAndReload(
            `Migrated. Copied ${copiedCount} ${copiedText}; skipped ${skippedCount} ${skippedText}.`,
            true,
          );
        });
      },
      () => {},
    );

    return false;
  },

  _syncButtonWasClicked(event) {
    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Syncing', 'Synced');
    const i9n = this.model;
    i9n.syncI9n((err, response) => {
      if (err) {
        $button.loadingDidFinishWithError(reqId);
      } else if (response?.message) {
        new app.AlertView().show(response.message, null, () => {
          window.location.reload();
        });
        $button.loadingDidFinishWithError(reqId);
      } else {
        $button.loadingDidFinishSuccessfully(reqId);
        app.showSavedToastAndReload('Synced!', i9n.isPosI9n());
        window.location.reload();
      }
    });
    return false;
  },

  _editButtonWasClicked() {
    this.trigger(app.ModalView.Events.ModalViewShouldHide, this);
    const modal = new app.I9nCredentialsDetailsView({ model: this.model });
    app.modalManager.showModalWithView(modal);
    return false;
  },

  _connectButtonWasClicked(event) {
    const $button = $(event.target);
    $button.initLoadingButton($button.html(), 'Connecting');
    window.location = this.model.buildOAuthUrl();

    return false;
  },

  _setUpWebhooksButtonWasClicked() {
    window.location = `/${app.org.get('normalizedName')}/#org-settings`;
  },

  _viewAccountButtonWasClicked(event) {
    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Connecting');
    const i9n = this.model;
    i9n.getLoginLink((err, response) => {
      if (err) {
        $button.loadingDidFinishWithError(reqId);
      } else {
        $button.loadingDidFinishSuccessfully(reqId);
        window.open(response.url, '_blank');
      }
    });

    return false;
  },

  _getOrderStatusUpdateMethod(i9n, i9nSchema) {
    if (!i9nSchema.supportsOrderStatusPolling && !i9nSchema.supportsOrderStatusWebhooks) {
      throw new Error('schema does not support order status updates');
    }
    if (!i9n.get('notifyUserOfOrderStatusUpdates')) {
      return 'NO';
    }

    if (
      !i9nSchema.supportsOrderStatusPolling ||
      (i9nSchema.supportsOrderStatusWebhooks && i9n.get('useWebhooksForOrderStatuses'))
    ) {
      return 'Webhooks';
    }

    return 'Polling';
  },

  render() {
    app.CellView.prototype.render.apply(this, arguments);

    const i9n = this.model;
    const i9nSchema = i9n.getI9nSchema();
    const neededActions = i9n.getNeededActions();
    let i9nStatus = null;
    if (this.model.requiresOAuth() && this.model.hasOAuthed()) {
      i9nStatus = `Connected to ${i9n.displayName()}`;
    }
    let nameHtml = `${getBadgeHtml(i9n.getI9nSchema())}&nbsp;${i9n.displayName()}`;

    const infoParts = [];
    const locationName = i9n.getLocationName();
    if (locationName) {
      infoParts.push(`location: ${locationName}`);
    }

    switch (i9n.get('system')) {
      case IntegrationSystem.Omnivore:
        infoParts.push(`locationID: ${i9n.get('locationID')}`);
        infoParts.push(OmnivorePosTypeHelper.name(i9n.get('posType')));
        break;
      case IntegrationSystem.Olo:
        // locationID resolves to a synced location name, already shown above.
        break;
      case IntegrationSystem.Punchh:
        if (i9n.get('enableAuthedLoyalty')) {
          infoParts.push('Authed Loyalty Enabled');
        }
        break;
      case IntegrationSystem.Smg:
        infoParts.push(`storeID: ${i9n.get('storeID')}`);
        break;
      case IntegrationSystem.Revel:
        infoParts.push(`Establishment ID: ${i9n.get('establishmentId')}`);
        if (i9n.hasArr('menuIds')) {
          const menuNames = i9n.get('menuIds').map((menuId) => {
            return i9n.getSyncedProp('menuById')[menuId]?.name || 'Unknown Menu';
          });
          infoParts.push(`Menus: ${_.sortBy(menuNames).join(', ')}`);
        }
        break;
      case IntegrationSystem.CardConnect:
        infoParts.push(`MID: ${i9n.get('merchantId')}`);
        break;
      case IntegrationSystem.Tcc:
        infoParts.push(`ClientId: ${i9n.get('clientId')}`);
        break;
      case IntegrationSystem.PaytronixOlo:
        infoParts.push(`Merchant ID: ${i9n.get('merchantID')}`);
        break;
      case IntegrationSystem.Worldpay:
        infoParts.push(`Account ID: ${i9n.get('accountId')}`);
        infoParts.push(`Debit enabled: ${!!i9n.get('enableDebitSupport')}`);
        break;
      case IntegrationSystem.Givex:
      case IntegrationSystem.GivexLoyalty:
        infoParts.push(GivexApiServerHelper.name(i9n.get('apiServer')));
        break;
      case IntegrationSystem.PinataPos:
      case IntegrationSystem.PinataLoyalty:
      case IntegrationSystem.PinataStoredValue:
      case IntegrationSystem.PinataNotifier:
      case IntegrationSystem.PinataFulfillment:
        infoParts.push(PinataApiServerHelper.name(i9n.get('apiServer')));
        break;
      case IntegrationSystem.ParBrink:
        infoParts.push(BrinkApiServerHelper.name(i9n.get('apiServer')));
        if (i9n.get('alternateMenuId')) {
          const { name } = (i9n.getSyncedProp('menuById') || {})[i9n.get('alternateMenuId')] || {};
          infoParts.push(`Menu: ${name}`);
        }
        break;
      case IntegrationSystem.Eigen:
        infoParts.push(EigenApiServerHelper.name(i9n.get('apiServer')));
        break;
      case IntegrationSystem.ParBrinkLoyaltyEarner:
        infoParts.push(`groupToken: ${i9n.get('groupToken')}`);
        break;
      case IntegrationSystem.Heartland:
        infoParts.push(`Site ID: ${i9n.get('heartlandSiteId')}`);
        break;
      case IntegrationSystem.Stripe:
      case IntegrationSystem.StripeTerminal:
        if (this.model.requiresOAuth() && this.model.hasOAuthed()) {
          infoParts.push(`Account: ${this.model.getAuthData().stripeAccountId}`);
        }
        break;
      case IntegrationSystem.Elavon:
        ['terminalId', 'locationName', 'chainCode'].forEach((fieldName) => {
          infoParts.push(`${i9nSchema.fields[fieldName].displayName}: ${i9n.get(fieldName)}`);
        });
        break;
      case IntegrationSystem.Toast:
        if (i9n.hasArr('menuIds')) {
          const menuNames = i9n.get('menuIds').map((menuId) => {
            return i9n.getSyncedProp('menuById')[menuId]?.name || 'Unknown Menu';
          });
          infoParts.push(`Menus: ${_.sortBy(menuNames).join(', ')}`);
        }
        break;
      case IntegrationSystem.ChoptLoyalty:
        infoParts.push(`locationID: ${i9n.get('choptLocationID')}`);
        break;
      case IntegrationSystem.LovesRewards: {
        infoParts.push(`Store ID: ${i9n.get('storeId')}`);
        break;
      }
      case IntegrationSystem.PeetsPaytronixOlo:
      case IntegrationSystem.PaytronixGift:
      case IntegrationSystem.PaytronixCompCard:
      case IntegrationSystem.PaytronixLoyalty:
        infoParts.push(`Merchant ID: ${i9n.get('merchantID')}`);
        infoParts.push(`Paytronix Store Code: ${i9n.get('storeCode')}`);
        break;
      case IntegrationSystem.Sparkfly:
      case IntegrationSystem.SparkflyLoyaltyEarner:
        infoParts.push(`Channel ID: ${i9n.get('channelId')}`);
        break;
      case IntegrationSystem.WorldpayStoredValue:
        infoParts.push(`Gift Card Type: ${i9n.get('giftCardType')}`);
        break;
      case IntegrationSystem.QsrAutomations:
        infoParts.push(`Mode: ${QsrAutomationsModeHelper.name(i9n.get('qsrAutomationsMode'))}`);
        infoParts.push(`Enterprise API: ${!!i9n.get('useEnterpriseApi')}`);
        if (i9n.get('useEnterpriseApi')) {
          if (i9n.get('qsrAutomationsCompanyUid')) {
            infoParts.push(`Company UID: ${i9n.get('qsrAutomationsCompanyUid')}`);
          }
          if (i9n.get('qsrAutomationsSiteUid')) {
            infoParts.push(`Site UID: ${i9n.get('qsrAutomationsSiteUid')}`);
          }
        }
        break;
      case IntegrationSystem.Infor:
        infoParts.push(`Store Id: ${i9n.get('storeId')}`);
        break;
      case IntegrationSystem.CbordCampusCard:
        infoParts.push(`Institution ID: ${i9n.get('institutionId')}`);
        break;
      case IntegrationSystem.QuickchargeCampusCard:
        infoParts.push(`Base URL: ${i9n.get('baseUrl')}`);
        break;
      case IntegrationSystem.Como:
      case IntegrationSystem.ComoOlo:
      case IntegrationSystem.ComoEarner:
        infoParts.push(`Business ID: ${i9n.get('businessID')}`);
        infoParts.push(`Branch ID: ${i9n.get('branchID')}`);
        break;
      case IntegrationSystem.Incentivio:
      case IntegrationSystem.Otter:
        if (!locationName) {
          infoParts.push(`Location ID: ${i9n.get('locationID')}`);
        }
        break;
      case IntegrationSystem.Sprinkles:
        infoParts.push(`Store ID: ${i9n.get('storeId')}`);
        break;
      case IntegrationSystem.LoyaltyPlant:
      case IntegrationSystem.LoyaltyPlantEarner:
        infoParts.push(`Establishment ID: ${i9n.get('establishmentId')}`);
        break;
      case IntegrationSystem.PDI:
        infoParts.push(`Site ID: ${i9n.get('siteID')}`);
        break;
      case IntegrationSystem.Adyen:
        infoParts.push(`Merchant Account: ${i9n.get('merchantAccount')}`);
        infoParts.push(`Store: ${i9n.get('store')}`);
        break;
      case IntegrationSystem.FreshKDS:
        infoParts.push(`On-Prem: ${!!i9n.get('freshKdsUseOnPremConnection')}`);
        if (!locationName && i9n.get('locationID')) {
          infoParts.push(`Location ID: ${i9n.get('locationID')}`);
        }
        break;
      case IntegrationSystem.LrsTableTracker:
        infoParts.push(`Gateway Serial: ${i9n.get('gatewaySerialNumber')}`);
        break;
      case IntegrationSystem.Appfront:
        infoParts.push(`External ID: ${i9n.get('externalId')}`);
        break;
      case IntegrationSystem.StarbucksStoredValue:
      case IntegrationSystem.StarbucksFullLoyalty:
        infoParts.push(`Store ID: ${i9n.get('storeMerchantId')}`);
        infoParts.push(`Starbucks Merchant ID: ${i9n.get('starbucksMerchantId')}`);
        infoParts.push(`Terminal ID: ${i9n.get('terminalId')}`);
        break;
      case IntegrationSystem.StarbucksPos:
        if (!locationName) {
          infoParts.push(`Store ID: ${i9n.get('locationID')}`);
        }
        infoParts.push(`Concept: ${StarbucksConceptHelper.name(i9n.get('starbucksConcept'))}`);
        infoParts.push(`Register: ${i9n.get('registerNumber')}`);
        break;
      case IntegrationSystem.LovesPos:
        if (!locationName) {
          infoParts.push(`Store ID: ${i9n.get('locationID')}`);
        }
        break;
      // Lower-confidence identifiers taken from each schema's searchField.
      case IntegrationSystem.ParPay:
      case IntegrationSystem.AurusPay:
        infoParts.push(`Corp ID: ${i9n.get('corpId')}`);
        break;
      case IntegrationSystem.ThanxOlo:
      case IntegrationSystem.Thanx:
      case IntegrationSystem.Moneris:
        if (i9n.get('merchantId')) {
          infoParts.push(`Merchant ID: ${i9n.get('merchantId')}`);
        }
        break;
      case IntegrationSystem.PinataTableTracker:
      case IntegrationSystem.Jtech:
        infoParts.push(`Section: ${i9n.get('sectionNumber')}`);
        break;
      case IntegrationSystem.SmoothCommerce:
        if (!locationName) {
          infoParts.push(`Location ID: ${i9n.get('locationID')}`);
        }
        break;
    }
    if (i9nSchema.type === 'loyalty' && i9n.hasArr('authMethods')) {
      const authMethodNames = i9n.get('authMethods').map((authMethod) => {
        return LoyaltyAuthMethodHelper.name(authMethod);
      });
      infoParts.push(`Auth methods: ${authMethodNames.join(', ')}`);
    }
    if (i9n.has('instanceType')) {
      infoParts.push(IntegrationInstanceTypeHelper.name(i9n.get('instanceType')));
    }
    if (i9nSchema.supportsOrderStatusPolling || i9nSchema.supportsOrderStatusWebhooks) {
      infoParts.push(`Order status updates: ${this._getOrderStatusUpdateMethod(i9n, i9nSchema)}`);
    }

    if (
      (app.location.get('couponProvider') === CouponProvider.Loyalty &&
        i9n.getI9nSchema().type === 'loyalty') ||
      (app.location.get('couponProvider') === CouponProvider.Pos &&
        i9n.getI9nSchema().type === 'pos')
    ) {
      infoParts.push('Used for coupons');
    }
    if (infoParts.length) {
      nameHtml += ` (${infoParts.join(', ')})`;
    }

    if (i9nStatus || neededActions.length) {
      nameHtml += '<br /><br />';
      if (neededActions.length) {
        nameHtml += `<span class="warning">${neededActions.join('<br />')}</span>`;
      } else {
        nameHtml += i9nStatus;
      }
    }

    this.$('.name').html(nameHtml);

    const $cellRow = $('<div class="cell-row"></div>');
    const $div = $('<div style="padding: 0 10px;"></div>');
    $cellRow.append($div);
    this.$el.append($cellRow);
    if (i9n.canBeSynced()) {
      const syncInfo = i9n.get('syncInfo') || {};
      let isWarning = true;
      let syncMessage = 'Never synced';
      if (syncInfo.syncedAt) {
        const timestamp = TimeHelper.displayDateFromTimestamp(
          syncInfo.syncedAt,
          app.location.get('timezone'),
        );
        syncMessage = `Last synced on: ${timestamp}`;
        // Warn if this location hasn't been synced for over a day
        isWarning = Date.now() - syncInfo.syncedAt > Time.DAY;
      }
      if (isWarning) {
        $div.append(`<p><span class="warning">${syncMessage}</span></p>`);
      } else {
        $div.append(`<p>${syncMessage}</p>`);
      }
      if (syncInfo.automaticSyncIncidentLastError) {
        const { occurredAt, description, debugMessage, mdCode } =
          syncInfo.automaticSyncIncidentLastError;
        const errorMessage = debugMessage ?? `${description} (${mdCode})`;
        const occurredAtStr = occurredAt
          ? ` (${TimeHelper.displayDateFromTimestamp(occurredAt, app.location.get('timezone'))})`
          : '';
        $div.append(
          `<span class="warning">Sync Incident${occurredAtStr}:<br />${errorMessage}</span>`,
        );
      }
    }

    if (i9n.isTwilio() && app.sessionUser.canManageSomeI9ns()) {
      let twilioDetails = 'Sender Phone Number: Unset';
      const senderPhoneNumberId = i9n.get('senderPhoneNumberId');

      if (senderPhoneNumberId) {
        const phoneNumberById = i9n.getSyncedProp('phoneNumberById');
        if (phoneNumberById) {
          const phoneNumberData = phoneNumberById[senderPhoneNumberId];
          if (phoneNumberData && phoneNumberData.phoneNumber) {
            twilioDetails = `Sender Phone Number: ${phoneNumberData.phoneNumber}`;
          }
        }
      }

      $div.append(`<p>${twilioDetails}</p>`);
    }

    const details = [];
    if (i9nSchema.type === 'pos') {
      details.push(`Sync sections: ${!!i9n.get('syncSections')}`);
      if (i9n.get('syncMenuStructures')) {
        details.push(`Sync menus: true`);
      }
      if (i9n.get('syncBadges')) {
        details.push(`Sync badges: true`);
      }
      details.push(`Supports tips: ${!!i9n.supportsTips()}`);
      details.push(`Supports Item Recipient: ${!!i9n.supportsItemRecipient()}`);
      const partialI9n = app.location.get('integrationById')[i9n.id];
      const locationSyncedData = (partialI9n || {}).syncedData || {};
      if (_.has(locationSyncedData, 'specialRequestMaxLength')) {
        details.push(`Special request max length: ${locationSyncedData.specialRequestMaxLength}`);
      }
    } else if (i9nSchema.type === 'loyalty') {
      details.push(`Send order without auth: ${!!i9n.get('sendOrderWithoutAuth')}`);
    }
    if (details.length) {
      $div.append(`<pre>${details.join('\n')}</pre>`);
    }

    if (i9n.getSyncedProp('dayParts')?.length) {
      const dayParts = i9n.getSyncedProp('dayParts').map((dayPartData) => {
        return DayPartHelper.displayDayPartData(dayPartData);
      });
      $div.append(`<pre alt="Day Parts">${dayParts.join('\n\n')}</pre>`);
    }
    if (i9n.getSyncedProp('unmappedOpenHoursTimetables')?.length) {
      const unmappedOpenHoursTimetables = i9n
        .getSyncedProp('unmappedOpenHoursTimetables')
        .map(({ posId, daySchedules }) => {
          return DayPartHelper.displayOpenHoursTimetableData({
            posId,
            daySchedules: daySchedules ?? [],
          });
        });
      $div.append(
        `<pre alt="Unmapped Open Hours">${unmappedOpenHoursTimetables.join('\n\n')}</pre>`,
      );
    }
    if (i9n.getSyncedProp('unmappedOpenHoursOverrides')?.length) {
      const overrides = i9n.getSyncedProp('unmappedOpenHoursOverrides').map((openHoursOverride) => {
        return DayPartHelper.displayOpenHoursOverrideData(
          openHoursOverride,
          app.location.get('timezone'),
        );
      });
      $div.append(`<pre alt="Unmapped Open Hour Overrides">${overrides.join('\n\n')}</pre>`);
    }

    if (i9n.hasStr('notes')) {
      $div.append(`<p>Notes: ${i9n.get('notes').split('\n').join('<br>')}</p>`);
    }

    return this;
  },
});
